import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  RefreshControl,
  Image,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFonts } from 'expo-font';
import { 
  getEstampDashboard,
  getEstampEvents,
  redeemEstampReward,
  getEstampHistory 
} from '../services/estampsApi';
import AppHeader from '../components/AppHeader';

const { width, height } = Dimensions.get('window');
import { API_BASE_URL } from '../config/api';

export default function EstampScreen({ navigation }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [eventsData, setEventsData] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Custom Alert Modal State
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error', // 'error', 'success', 'confirm'
    onConfirm: null
  });

  const [fontsLoaded] = useFonts({
    'Poppins': require('../../assets/fonts/Poppins-Regular.ttf'),
    'Poppins-Medium': require('../../assets/fonts/Poppins-Medium.ttf'),
    'Poppins-SemiBold': require('../../assets/fonts/Poppins-SemiBold.ttf'),
    'Poppins-Bold': require('../../assets/fonts/Poppins-Bold.ttf'),
  });

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      
      if (!userToken || !sessionId) {
        navigation.navigate('Login');
        return;
      }

      const storedUserData = await AsyncStorage.getItem('userData');
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
        
        // Load events first to get available brands and return default brand_id
        const defaultBrandId = await loadEventsData();
        
        // Use the returned brand_id from loadEventsData for dashboard
        const validatedBrandId = isValidBrandId(defaultBrandId) ? defaultBrandId : null;
        // console.log('loadUserData - Loading dashboard with brandId:', validatedBrandId);
        console.log('loadUserData - Loading dashboard with brandId');
        await loadDashboard(validatedBrandId);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      showCustomAlert('Error', 'Failed to load user data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Custom Alert Functions
  const showCustomAlert = (title, message, type = 'error', onConfirm = null) => {
    setAlertModal({
      visible: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  // Helper function to validate brand ID
  const isValidBrandId = (brandId) => {
    return brandId !== null && brandId !== undefined && brandId !== '' && brandId !== '0' && brandId !== 0;
  };

  const hideCustomAlert = () => {
    setAlertModal(prev => ({ ...prev, visible: false, onConfirm: null }));
  };

  const handleAlertConfirm = () => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    hideCustomAlert();
  };

  const loadDashboard = async (brandId = null) => {
    try {
      console.log('Loading dashboard with brandId:', brandId);
      const result = await getEstampDashboard(brandId);
      if (result.success) {
        setDashboardData(result.data);
        console.log('Dashboard loaded successfully');
      } else {
        console.error('Failed to load dashboard data:', result.error);
        // Show error to user if it's not a brand-related issue (API handles brand fallback)
        if (result.error && !result.error.includes('Brand not found')) {
          showCustomAlert('Error', 'Failed to load dashboard data', 'error');
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showCustomAlert('Error', 'Failed to load dashboard data', 'error');
    }
  };

  const loadEventsData = async (brandId = null) => {
    try {
      // console.log('Loading events with brandId:', brandId);
      console.log('Loading events with brandId');
      const result = await getEstampEvents(brandId);
      if (result.success && Array.isArray(result.data)) {
        setEventsData(result.data);
        console.log('Events loaded successfully, count:', result.data.length);
        
        // Set default brand if none selected and events exist
        if (!selectedBrand && result.data.length > 0) {
          const availableBrands = getAvailableBrandsFromEvents(result.data);
          if (availableBrands.length > 0) {
            const defaultBrand = availableBrands[0];
            setSelectedBrand(defaultBrand.brand_name);
            setSelectedBrandId(defaultBrand.brand_id);
            console.log('Set default brand:', defaultBrand.brand_name, 'ID:', defaultBrand.brand_id);
            return defaultBrand.brand_id; // Return the default brand_id
          }
        }
      } else {
        setEventsData([]);
        console.error('Failed to load events data:', result.error);
        // Show error to user if it's not a brand-related issue (API handles brand fallback)
        if (result.error && !result.error.includes('Brand not found')) {
          showCustomAlert('Error', 'Failed to load events data', 'error');
        }
        return null;
      }
    } catch (error) {
      console.error('Error loading events data:', error);
      setEventsData([]);
      showCustomAlert('Error', 'Failed to load events data', 'error');
      return null;
    }
    return null; // Default return for cases where no brand is set
  };

  // Get unique brands from events data
  const getAvailableBrandsFromEvents = (events = eventsData) => {
    const brandMap = new Map();
    
    events.forEach(event => {
      if (event.brand_name && event.brand_id) {
        const brandKey = `${event.brand_id}-${event.brand_name}`;
        if (!brandMap.has(brandKey)) {
          brandMap.set(brandKey, {
            brand_id: event.brand_id,
            brand_name: event.brand_name,
            brand_logo: event.brand_logo || null
          });
        }
      }
    });
    
    return Array.from(brandMap.values()).sort((a, b) => a.brand_name.localeCompare(b.brand_name));
  };

  const getSelectedBrandStamps = () => {
    // More defensive approach for production builds
    try {
      // First, try to get total stamps from user balance as primary fallback
      const totalStamps = dashboardData?.user_balance?.total_estamp || 
                         dashboardData?.user_balance?.total_estamps || 
                         dashboardData?.total_estamp || 
                         dashboardData?.total_estamps || 0;
      
      // console.log('Debug - Total stamps from dashboard:', totalStamps);
      // console.log('Debug - Selected brand:', selectedBrand, 'Selected brand ID:', selectedBrandId);
      // console.log('Debug - Dashboard data:', dashboardData);
      
      // If no dashboard data or brand collections, return total stamps
      if (!dashboardData || !Array.isArray(dashboardData.brand_collections) || dashboardData.brand_collections.length === 0) {
        // console.log('Debug - No dashboard data or brand collections, returning total stamps:', totalStamps);
        return totalStamps;
      }
      
      // If no brand is selected, return total stamps
      if (!selectedBrandId || !selectedBrand) {
        // console.log('Debug - No brand selected, returning total stamps:', totalStamps);
        return totalStamps;
      }
      
      // Find the selected brand collection
      console.log('Debug - Brand collections:', dashboardData.brand_collections);
      const selectedBrandCollection = dashboardData.brand_collections.find(
        collection => {
          // More flexible matching
          const matches = collection && (
            collection.brand_id === selectedBrandId ||
            collection.brand_id === String(selectedBrandId) ||
            collection.brand_name === selectedBrand
          );
          // console.log(`Debug - Checking collection:`, collection, 'Matches:', matches);
          return matches;
        }
      );
      
      // console.log('Debug - Selected brand collection:', selectedBrandCollection);
      
      // If brand collection not found, return total stamps
      if (!selectedBrandCollection) {
        // console.log('Debug - Brand collection not found, returning total stamps:', totalStamps);
        return totalStamps;
      }
      
      // Try multiple possible property names for brand-specific stamps
      const brandStamps = selectedBrandCollection.total_estamps || 
                         selectedBrandCollection.total_estamp || 
                         selectedBrandCollection.estamps || 
                         selectedBrandCollection.estamp_count || 
                         selectedBrandCollection.stamps || 
                         selectedBrandCollection.count || 
                         0;
      
      // console.log('Debug - Brand stamps calculated:', brandStamps);
      
      // Return brand-specific stamps if available, otherwise total stamps
      const finalStamps = brandStamps > 0 ? brandStamps : totalStamps;
      // console.log('Debug - Final stamps returned:', finalStamps);
      return finalStamps;
      
    } catch (error) {
      console.error('Error in getSelectedBrandStamps:', error);
      // Ultimate fallback
      return 0;
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    console.log('onRefresh - selectedBrandId:', selectedBrandId);
    
    // Validate brand ID before making API calls
    const validatedBrandId = isValidBrandId(selectedBrandId) ? selectedBrandId : null;
    console.log('onRefresh - validatedBrandId:', validatedBrandId);
    
    await loadUserData();
    await loadDashboard(validatedBrandId);
    await loadEventsData(validatedBrandId);
    setRefreshing(false);
  };

  const handleExchange = (eventId) => {
    console.log('Debug - handleExchange called with eventId:', eventId);
    console.log('Debug - selectedBrandId:', selectedBrandId);
    setSelectedEventId(eventId);
    
    // Pass the event ID to performExchange as required by the API
    showCustomAlert(
      'Confirm Exchange',
      'Are you sure you want to exchange your stamps for this reward?',
      'confirm',
      () => performExchange(eventId)
    );
  };

  const performExchange = async (eventId) => {
    console.log('=== performExchange function called ===');
    console.log('Event ID passed:', eventId);
    console.log('Selected Event ID:', selectedEventId);
    
    // Use the passed eventId or fall back to selectedEventId
    const targetEventId = eventId || selectedEventId;
    
    if (!targetEventId) {
      console.error('No event ID selected for exchange');
      showCustomAlert('Error', 'No event selected for exchange', 'error');
      return;
    }
    
    try {
      console.log('Calling redeemEstampReward API with eventId:', targetEventId);
      const result = await redeemEstampReward(targetEventId);
      console.log('API response:', result);
      
      if (result.success) {
        console.log('Exchange successful, showing success alert');
        
        // Check if coupon was claimed and create appropriate success message
        let successMessage = 'E-Stamps exchanged successfully!';
        if (result.data && result.data.coupon) {
          const coupon = result.data.coupon;
          successMessage = `E-Stamps exchanged successfully!\n\nBonus: You received a coupon!\nCode: ${coupon.code || coupon.kode_kupon || 'N/A'}\nDescription: ${coupon.description || coupon.nama_kupon || 'Discount coupon'}`;
        } else if (result.data && result.data.coupon_claimed) {
          const coupon = result.data.coupon_claimed;
          successMessage = `E-Stamps exchanged successfully!\n\nBonus: You received a coupon!\nCode: ${coupon.kode_kupon || 'N/A'}\nDescription: ${coupon.nama_kupon || 'Discount coupon'}`;
        }
        
        showCustomAlert('Success', successMessage, 'success');
        
        // Reload dashboard with selected brand ID to refresh stamp counts
        console.log('Reloading dashboard and events data...');
        await loadDashboard(selectedBrandId);
        await loadEventsData(selectedBrandId);
        console.log('Data reload completed');
      } else {
        console.error('Exchange failed:', result.error);
        showCustomAlert('Error', result.error || 'Failed to exchange E-Stamps', 'error');
      }
    } catch (error) {
      console.error('Error exchanging E-Stamps:', error);
      showCustomAlert('Error', 'Failed to exchange E-Stamps', 'error');
    }
  };

  const handleBrandSelect = (brand, brandId) => {
    console.log('handleBrandSelect - brand:', brand, 'brandId:', brandId);
    setSelectedBrand(brand);
    setSelectedBrandId(brandId);
    setShowDropdown(false);
    
    // Validate brand ID before making API calls
    const validatedBrandId = isValidBrandId(brandId) ? brandId : null;
    console.log('handleBrandSelect - validatedBrandId:', validatedBrandId);
    
    // Reload dashboard and events with validated brand ID
    loadDashboard(validatedBrandId);
    loadEventsData(validatedBrandId);
  };

  const renderBrandDropdown = () => {
    // Get brands from events data instead of dashboard brand_collections
    const availableBrands = getAvailableBrandsFromEvents();
    
    return (
      <View style={styles.bottomSheet}>
        <TouchableOpacity 
          style={styles.bottomSheet} 
          onPress={() => setShowDropdown(false)}
          activeOpacity={1}
        >
          <View style={styles.bottomSheetContent}>
            <View style={styles.handleBar} />
            <View style={styles.bottomSheetHeader}>
              <Text style={styles.bottomSheetTitle}>Select Brand</Text>
            </View>
            
            <View style={styles.brandListContainer}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {availableBrands.map((brand, index) => (
                  <View key={brand.brand_id}>
                    <TouchableOpacity
                      style={styles.brandItem}
                      onPress={() => handleBrandSelect(brand.brand_name, brand.brand_id)}
                    >
                      <View style={styles.brandInfo}>
                        {brand.brand_logo ? (
                          <Image 
                            source={{ uri: `${API_BASE_URL}/${brand.brand_logo}` }}
                            style={styles.brandIcon}
                            resizeMode="contain"
                          />
                        ) : (
                          <View style={styles.brandIcon}>
                            <Text style={styles.brandIconText}>{brand.brand_name.charAt(0)}</Text>
                          </View>
                        )}
                        <Text style={styles.brandName}>{brand.brand_name}</Text>
                      </View>
                      <View style={selectedBrand === brand.brand_name ? styles.radioButtonSelected : styles.radioButtonUnselected}>
                        {selectedBrand === brand.brand_name && <View style={styles.radioButtonDot} />}
                      </View>
                    </TouchableOpacity>
                    {index < availableBrands.length - 1 && <View style={styles.brandSeparator} />}
                  </View>
                ))}
              </ScrollView>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  if (!fontsLoaded || loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0F766E" />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader title="E-Stamps" onBack={() => navigation.goBack()} />

      <ScrollView 
        style={styles.content}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#0F766E']}
          />
        }
      >
        <View style={styles.pointCard}>
          <View style={styles.pointCardContent}>
            <View style={styles.backgroundCircle} />
            <View style={styles.pointCardHeader}>
              <View style={styles.pointCardLeft}>
                <Text style={styles.pointCardTitle}>Current Brand</Text>
                <View style={styles.dropdownContainer}>
                  <TouchableOpacity 
                    style={styles.brandSelector}
                    onPress={() => setShowDropdown(true)}
                  >
                    <Text style={styles.brandSelectorText}>{selectedBrand}</Text>
                    <Ionicons name="chevron-down" size={16} color="#F8FAFC" />
                  </TouchableOpacity>
                </View>
              </View>
              <View style={styles.pointCardRight}>
                <Text style={styles.pointCardLabel}>E-Stamps</Text>
                <View style={styles.pointsContainer}>
                  <View style={styles.coinContainer}>
                    <Ionicons name="star" size={12} color="#F59E0B" />
                  </View>
                  <Text style={styles.pointsValue}>
                    {getSelectedBrandStamps()}
                  </Text>
                  <Text style={styles.pointsUnit}>stamps</Text>
                </View>
              </View>
            </View>
            
            <View style={styles.stampGrid}>
              {(() => {
                // Get events for selected brand to determine max stamps and redeemable positions
                const brandEvents = eventsData.filter(event => 
                  !selectedBrand || event.brand_name === selectedBrand
                );
                
                // Find maximum required stamps for this brand
                const maxStamps = brandEvents.length > 0 
                  ? Math.max(...brandEvents.map(event => event.required_stamps))
                  : 10; // Default to 10 if no events
                
                // Get all redeemable positions (where events can be exchanged)
                const redeemablePositions = brandEvents.map(event => event.required_stamps);
                
                return Array.from({ length: maxStamps }, (_, index) => {
                  const stampNumber = index + 1;
                  const selectedBrandStamps = getSelectedBrandStamps();
                  const isChecked = selectedBrandStamps && stampNumber <= selectedBrandStamps;
                  const isRedeemable = redeemablePositions.includes(stampNumber);
                  
                  // Get selected brand logo from events data
                  const selectedBrandEvent = eventsData.find(event => 
                    event.brand_name === selectedBrand
                  );
                  const brandLogo = selectedBrandEvent?.brand_logo;
                  
                  return (
                    <View key={`stamp-${stampNumber}`} style={styles.stampItem}>
                       <View style={[
                         styles.stampCard, 
                         isChecked && styles.stampCardChecked,
                         isRedeemable && styles.stampCardGold
                       ]}>
                        {brandLogo ? (
                          <Image 
                            source={{ uri: `${API_BASE_URL}/${brandLogo}` }}
                            style={styles.stampBrandLogo}
                            resizeMode="contain"
                          />
                        ) : (
                          <View style={styles.stampBrandIcon}>
                            <Image 
                              source={require('../../assets/icons/star-icon.png')}
                              style={{ width: 20, height: 20, tintColor: '#FFFFFF' }}
                              resizeMode="contain"
                            />
                          </View>
                        )}
                        {isRedeemable ? (
                          <View style={styles.goldStar}>
                            <Ionicons name="star" size={12} color="#FFD700" />
                          </View>
                        ) : null}
                        {isChecked ? (
                          <View style={styles.checkIcon}>
                            <Ionicons name="checkmark" size={10} color="#FFFFFF" />
                          </View>
                        ) : null}
                      </View>
                    </View>
                  );
                });
              })()
              }
            </View>
          </View>
        </View>

        <TouchableOpacity 
          style={styles.historyButton}
          onPress={() => navigation.navigate('History')}
        >
          <Text style={styles.historyButtonText}>View E-Stamp History</Text>
          <Ionicons name="chevron-forward" size={16} color="#0F766E" />
        </TouchableOpacity>

        <View style={styles.eventsSection}>
          <Text style={styles.eventsSectionTitle}>Available Rewards</Text>
          {eventsData
            .filter(event => !selectedBrand || event.brand_name === selectedBrand)
            .map((event) => {
            const selectedBrandStamps = getSelectedBrandStamps();
            const canExchange = selectedBrandStamps >= event.required_stamps;
            
            // Debug logging
            console.log(`Event: ${event.name}, Required: ${event.required_stamps}, User has: ${selectedBrandStamps}, Can exchange: ${canExchange}`);
            
            return (
              <View key={event.id} style={styles.eventCard}>
                <View style={styles.eventImageContainer}>
                  {event.image ? (
                    <Image 
                      source={{ uri: `${API_BASE_URL}/uploads/estamp/${event.image}` }}
                      style={styles.eventImage}
                      resizeMode="cover"
                    />
                  ) : (
                    <View style={styles.eventImagePlaceholder}>
                      <Ionicons name="gift" size={24} color="#94A3B8" />
                    </View>
                  )}
                  {event.brand_logo && (
                    <Image 
                      source={{ uri: `${API_BASE_URL}/${event.brand_logo}` }}
                      style={styles.brandLogo}
                      resizeMode="contain"
                    />
                  )}
                </View>
                <View style={styles.eventContent}>
                  <Text style={styles.eventTitle}>{event.name || event.title}</Text>
                  {event.brand_name && (
                    <Text style={styles.brandName}>{event.brand_name}</Text>
                  )}
                  <Text style={styles.eventReward}>Reward: {event.reward}</Text>
                  <Text style={styles.eventStampCount}>Required: {event.required_stamps} E-Stamps</Text>
                  <TouchableOpacity
                    style={[
                      styles.exchangeButton,
                      canExchange ? styles.exchangeButtonActive : styles.exchangeButtonInactive
                    ]}
                    onPress={() => canExchange && handleExchange(event.id)}
                    disabled={!canExchange}
                  >
                    <Text style={[
                      styles.exchangeButtonText,
                      canExchange ? styles.exchangeButtonTextActive : styles.exchangeButtonTextInactive
                    ]}>
                      {canExchange ? 'Exchange' : 'Not Enough'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      {showDropdown && renderBrandDropdown()}
      
      {/* Custom Alert Modal */}
      <Modal
        visible={alertModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={hideCustomAlert}
      >
        <View style={styles.alertOverlay}>
          <View style={styles.alertContainer}>
            <View style={styles.alertHeader}>
              <Text style={styles.alertTitle}>{alertModal.title}</Text>
            </View>
            <View style={styles.alertBody}>
              <Text style={styles.alertMessage}>{alertModal.message}</Text>
            </View>
            <View style={styles.alertFooter}>
              {alertModal.type === 'confirm' ? (
                <>
                  <TouchableOpacity
                    style={[styles.alertButton, styles.alertButtonSecondary]}
                    onPress={hideCustomAlert}
                  >
                    <Text style={styles.alertButtonTextSecondary}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.alertButton, styles.alertButtonPrimary]}
                    onPress={handleAlertConfirm}
                  >
                    <Text style={styles.alertButtonTextPrimary}>Confirm</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <TouchableOpacity
                  style={[styles.alertButton, styles.alertButtonPrimary]}
                  onPress={hideCustomAlert}
                >
                  <Text style={styles.alertButtonTextPrimary}>OK</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  pointCard: {
    backgroundColor: '#22D3EE',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  pointCardContent: {
    position: 'relative',
  },
  backgroundCircle: {
    position: 'absolute',
    top: -50,
    right: -50,
    width: 150,
    height: 150,
    borderRadius: 75,
    borderWidth: 24,
    borderColor: '#FFFFFF',
    opacity: 0.24,
  },
  pointCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    zIndex: 9998,
    position: 'relative',
  },
  pointCardLeft: {
    flex: 1,
  },
  pointCardTitle: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    color: '#F8FAFC',
    marginBottom: 8,
  },
  dropdownContainer: {
    position: 'relative',
    zIndex: 9999,
  },
  brandSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#FFFFFF',
    alignSelf: 'flex-start',
  },
  bottomSheet: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999999,
    elevation: 999999,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  bottomSheetContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    width: '100%',
    maxHeight: '80%',
    alignItems: 'center',
  },
  handleBar: {
    width: 40,
    height: 6,
    backgroundColor: '#CBD5E1',
    borderRadius: 3,
    marginBottom: 16,
    alignSelf: 'center',
  },
  bottomSheetHeader: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginBottom: 20,
  },
  bottomSheetTitle: {
    fontSize: 18,
    fontFamily: 'Poppins-SemiBold',
    color: '#000000',
  },
  brandListContainer: {
    width: '100%',
    paddingVertical: 8,
    paddingBottom: 100,
    maxHeight: 400,
  },
  brandItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 0,
  },
  brandInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  brandIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#1E293B',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  brandIconText: {
    fontSize: 16,
    fontFamily: 'Poppins-SemiBold',
    color: '#FFFFFF',
  },
  brandName: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#1E293B',
  },
  radioButton: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#1E293B',
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
  },
  radioButtonUnselected: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  brandSeparator: {
    height: 1,
    backgroundColor: '#F1F5F9',
    width: '100%',
    marginVertical: 4,
  },
  brandSelectorText: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    color: '#F8FAFC',
    marginRight: 4,
  },
  pointCardRight: {
    alignItems: 'flex-end',
  },
  pointCardLabel: {
    fontSize: 16,
    fontFamily: 'Poppins-SemiBold',
    color: '#F8FAFC',
    marginBottom: 8,
  },
  pointsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  coinContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FEF3C7',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  pointsValue: {
    fontSize: 24,
    fontFamily: 'Poppins-Bold',
    color: '#F8FAFC',
    marginRight: 4,
  },
  pointsUnit: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    color: '#F8FAFC',
  },
  stampGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
  },
  stampItem: {
    width: '18%',
    marginBottom: 12,
  },
  stampCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    aspectRatio: 1,
    justifyContent: 'space-between',
    height: 80
  },
  stampCardChecked: {
     backgroundColor: 'rgba(255, 255, 255, 0.2)',
     borderColor: '#06B6D4',
   },
   stampCardGold: {
     backgroundColor: '#FEF3C7',
     borderColor: '#FFD700',
   },

  stampIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  stampBrandLogo: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  stampBrandIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#1E293B',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stampBrandText: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    color: '#FFFFFF',
  },
  checkIcon: {
    position: 'absolute',
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#10B981',
    justifyContent: 'center',
    alignItems: 'center',
    bottom: 22,
    left: '50%',
    marginLeft: -8,
  },
  goldStar: {
    position: 'absolute',
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FEF3C7',
    justifyContent: 'center',
    alignItems: 'center',
    top: -8,
    right: -8,
    borderWidth: 1,
    borderColor: '#FFD700',
  },
  historyButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    marginTop: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  historyButtonText: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    color: '#0F766E',
  },
  eventsSection: {
    marginTop: 8,
    paddingBottom: 100,
  },
  eventsSectionTitle: {
    fontSize: 18,
    fontFamily: 'Poppins-SemiBold',
    color: '#1E293B',
    marginBottom: 16,
  },
  eventCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  eventImageContainer: {
    marginRight: 12,
  },
  eventImagePlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  eventImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
  },
  brandLogo: {
    width: 20,
    height: 20,
    borderRadius: 10,
    position: 'absolute',
    bottom: -5,
    right: -5,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  eventContent: {
    flex: 1,
  },
  eventTitle: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    color: '#1E293B',
    marginBottom: 4,
    lineHeight: 20,
  },
  brandName: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#64748B',
    marginBottom: 2,
  },
  eventReward: {
    fontSize: 12,
    fontFamily: 'Poppins-Medium',
    color: '#0F766E',
    marginBottom: 4,
  },
  eventStampCount: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#64748B',
    marginBottom: 12,
  },
  exchangeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  exchangeButtonActive: {
    backgroundColor: '#0F766E',
  },
  exchangeButtonInactive: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  exchangeButtonText: {
    fontSize: 12,
    fontFamily: 'Poppins-SemiBold',
  },
  exchangeButtonTextActive: {
    color: '#FFFFFF',
  },
  exchangeButtonTextInactive: {
    color: '#94A3B8',
  },
  // Custom Alert Modal Styles
  alertOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  alertContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 320,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  alertHeader: {
    padding: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  alertTitle: {
    fontSize: 18,
    fontFamily: 'Poppins-SemiBold',
    color: '#1E293B',
    textAlign: 'center',
  },
  alertBody: {
    padding: 20,
    paddingTop: 12,
    paddingBottom: 24,
  },
  alertMessage: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
  },
  alertFooter: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  alertButton: {
    flex: 1,
    paddingVertical: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  alertButtonPrimary: {
    backgroundColor: '#0F766E',
    borderBottomRightRadius: 12,
  },
  alertButtonSecondary: {
    backgroundColor: '#F8FAFC',
    borderBottomLeftRadius: 12,
    borderRightWidth: 1,
    borderRightColor: '#F1F5F9',
  },
  alertButtonTextPrimary: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    color: '#FFFFFF',
  },
  alertButtonTextSecondary: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    color: '#64748B',
  },
});